import * as THREE from 'three';
import type { FlowSample, River } from '../game/River';
import { DUCK_RADIUS, type Tuning } from '../game/tuning';
import { buildAccessory, type DuckLook } from './DuckSkins';
import { disposeObject3D } from '../utils/dispose';

export type DuckEvents = {
  /** Impact speed into a rock (m/s), 0 if none this frame. */
  rockHit: number;
  /** Impact speed into a bank (m/s), 0 if none this frame. */
  bankHit: number;
  /** A whirlpool threw the duck out this frame. */
  ejected: boolean;
  /** Went over a waterfall lip this frame (not wave hops). */
  launched: boolean;
  /** Downward speed (m/s) on splashing down this frame, 0 if none. */
  landed: number;
  /** A hard rock hit spun the duck out this frame. */
  spunOut: boolean;
  /** The bubble shield absorbed a hit this frame. */
  shieldPopped: boolean;
};

const WHIRL_EJECT_SECONDS = 2.5;
const GRAVITY = 19;
/** Rock impacts faster than this (m/s) spin the duck out. */
const SPIN_OUT_IMPACT = 2;
/** Steering authority while airborne. */
const AIR_STEER = 0.45;

export function buildDuckModel(options: DuckLook & { ghost?: boolean } = {}): THREE.Group {
  const ghost = options.ghost ?? false;
  const metalness = options.metalness ?? 0;
  const material = (color: string) =>
    ghost
      ? new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35, depthWrite: false })
      : new THREE.MeshStandardMaterial({ color, roughness: metalness > 0 ? 0.25 : 0.35, metalness });

  const model = new THREE.Group();
  const yellow = material(ghost ? '#dff4ff' : (options.body ?? '#ffd21f'));
  const headMaterial = options.head && !ghost ? material(options.head) : yellow;
  const orange = material(ghost ? '#dff4ff' : (options.beak ?? '#ff8a1c'));

  const body = new THREE.Mesh(new THREE.SphereGeometry(0.5, 24, 16), yellow);
  body.scale.set(0.95, 0.72, 1.15);
  body.position.y = 0.22;
  model.add(body);

  const tail = new THREE.Mesh(new THREE.SphereGeometry(0.2, 14, 10), yellow);
  tail.scale.set(0.9, 0.7, 1.3);
  tail.position.set(0, 0.42, 0.5);
  tail.rotation.x = 0.7;
  model.add(tail);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.3, 20, 14), headMaterial);
  head.position.set(0, 0.66, -0.3);
  model.add(head);

  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.3, 12), orange);
  beak.scale.set(1.3, 1, 0.55);
  beak.rotation.x = -Math.PI / 2;
  beak.position.set(0, 0.6, -0.64);
  model.add(beak);

  if (!ghost) {
    const eyeMaterial = new THREE.MeshStandardMaterial({ color: '#111111', roughness: 0.2 });
    for (const side of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 10, 8), eyeMaterial);
      eye.position.set(side * 0.16, 0.74, -0.52);
      model.add(eye);
    }
    if (options.accessory) model.add(buildAccessory(options.accessory));
    model.traverse((object) => {
      if ((object as THREE.Mesh).isMesh) object.castShadow = true;
    });
  }

  return model;
}

export class Duck {
  readonly group = new THREE.Group();
  readonly position = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();
  readonly flow: FlowSample = { x: 0, z: 0, speed: 0, whirl: 0 };
  readonly events: DuckEvents = {
    rockHit: 0,
    bankHit: 0,
    ejected: false,
    launched: false,
    landed: 0,
    spunOut: false,
    shieldPopped: false,
  };

  private readonly model: THREE.Group;
  private readonly bubble: THREE.Mesh;
  /** Seconds of bubble shield left; the next hard hit pops it instead. */
  shieldTime = 0;
  private readonly shadow: THREE.Mesh;
  private readonly dir = { x: 0, z: -1 };
  private yaw = 0;
  private spin = 0;
  private roll = 0;
  private squash = 0;
  private bobTime = 0;
  private whirlTime = 0;
  private glide = 0;
  private vy = 0;
  private airborne = false;
  private stun = 0;
  /** Seconds of bounce-back left after a hit. */
  private recover = 0;
  /** Tuning from the last update, for contacts resolved by hazards. */
  private tune: Tuning | null = null;
  private spinVel = 0;
  private waterY = 0;
  private big = false;
  /** Vertical speed of the water surface under the duck, for wave hops. */
  private surfaceVy = 0;
  private lastClimb = 0;

  constructor(look: DuckLook = {}) {
    this.model = buildDuckModel(look);
    this.group.add(this.model);
    this.shadow = new THREE.Mesh(
      new THREE.CircleGeometry(0.62, 24),
      new THREE.MeshBasicMaterial({ color: '#06222a', transparent: true, opacity: 0.35, depthWrite: false }),
    );
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.position.y = 0.03;
    this.group.add(this.shadow);
    this.bubble = new THREE.Mesh(
      new THREE.SphereGeometry(0.85, 24, 16),
      new THREE.MeshStandardMaterial({
        color: '#bfe9ff',
        transparent: true,
        opacity: 0.28,
        roughness: 0.05,
        emissive: '#6fd0ff',
        emissiveIntensity: 0.25,
        depthWrite: false,
      }),
    );
    this.bubble.position.y = 0.35;
    this.bubble.visible = false;
    this.group.add(this.bubble);
  }

  /** Swap the duck's look (a new skin) without touching its physics. */
  restyle(look: DuckLook): void {
    const fresh = buildDuckModel(look);
    for (const child of [...this.model.children]) {
      this.model.remove(child);
      disposeObject3D(child);
    }
    for (const child of [...fresh.children]) this.model.add(child);
  }

  /** A big shove forward with a long glide (the squeaky boost). */
  boost(kick: number, glide: number, river: River, tuning: Tuning): void {
    this.paddle(kick, river, tuning);
    this.glide = Math.max(this.glide, glide);
  }

  get heading(): number {
    return this.yaw + this.spin;
  }

  get distance(): number {
    return -this.position.z;
  }

  get inAir(): boolean {
    return this.airborne;
  }

  get stunned(): boolean {
    return this.stun > 0;
  }

  reset(x: number, y: number, z: number): void {
    this.position.set(x, y, z);
    this.waterY = y;
    this.vy = 0;
    this.airborne = false;
    this.big = false;
    this.lastClimb = 0;
    this.surfaceVy = 0;
    this.stun = 0;
    this.recover = 0;
    this.spinVel = 0;
    this.shieldTime = 0;
    this.velocity.set(0, 0, 0);
    this.yaw = 0;
    this.spin = 0;
    this.roll = 0;
    this.squash = 0;
    this.whirlTime = 0;
    this.glide = 0;
    this.syncVisuals(0);
  }

  /** Kick forward along the river; the duck glides (loses less speed) for a moment after. */
  paddle(kick: number, river: River, tuning: Tuning): boolean {
    const dir = river.direction(this.distance, this.dir);
    this.velocity.x += dir.x * kick;
    this.velocity.z += dir.z * kick;
    this.glide = tuning.glideTime;
    this.squash = Math.min(1, this.squash + 0.45);
    // Paddling off a rising wave (or mid-hop) launches you: jump rocks in the rapids.
    const onWave = this.airborne ? !this.big : this.lastClimb > 0.6;
    if (onWave) {
      this.airborne = true;
      this.big = false;
      this.vy = Math.max(this.vy, 0) + 3.4;
    }
    return onWave;
  }

  /** A crumb's little shove forward (no glide, no wave jump). */
  nibble(kick: number, river: River): void {
    const dir = river.direction(this.distance, this.dir);
    this.velocity.x += dir.x * kick;
    this.velocity.z += dir.z * kick;
    this.squash = Math.min(1, this.squash + 0.12);
  }

  /** Speed (m/s) along the river, downstream positive. */
  forwardSpeed(river: River): number {
    const dir = river.direction(this.distance, this.dir);
    return this.velocity.x * dir.x + this.velocity.z * dir.z;
  }

  /** Slow enough that paddling is a free flap rather than a charge. */
  canFlap(river: River, tuning: Tuning): boolean {
    return this.forwardSpeed(river) < tuning.flapCap;
  }

  /**
   * A free flap when stalled: a small push forward that fades out as the
   * duck nears `flapCap`, so it gets you unstuck but never replaces a paddle.
   * Returns the push applied (0 when already moving well, stunned or in the air).
   */
  flap(river: River, tuning: Tuning): number {
    if (this.airborne || this.stun > 0) return 0;
    const kick = tuning.flapKick * Math.max(0, Math.min(1, (tuning.flapCap - this.forwardSpeed(river)) / 1.5));
    const dir = river.direction(this.distance, this.dir);
    if (kick <= 0) return 0;
    this.velocity.x += dir.x * kick;
    this.velocity.z += dir.z * kick;
    this.squash = Math.min(1, this.squash + 0.2);
    return kick;
  }

  get gliding(): boolean {
    return this.glide > 0;
  }

  /**
   * Resolve a touch against a solid (rock, log, wall, beak): move the duck to
   * (px, pz) outside it along normal (nx, nz). (ovx, ovz) is the obstacle's own
   * velocity. Hard impacts bounce and can spin out; gentle ones slide.
   */
  contact(nx: number, nz: number, px: number, pz: number, ovx: number, ovz: number): void {
    this.position.x = px;
    this.position.z = pz;
    const vn = (this.velocity.x - ovx) * nx + (this.velocity.z - ovz) * nz;
    if (vn < -0.8 && this.shieldTime > 0) {
      // The bubble takes the hit: bounce off without losing speed.
      this.velocity.x -= nx * vn * 1.6;
      this.velocity.z -= nz * vn * 1.6;
      this.popShield();
    } else if (vn < -0.8) {
      // A real impact: bounce off and lose speed.
      this.velocity.x -= nx * vn * 1.35;
      this.velocity.z -= nz * vn * 1.35;
      this.velocity.multiplyScalar(this.tune?.hitKeep ?? 0.6);
      this.glide = 0;
      this.recover = this.tune?.recoverTime ?? 0;
      this.events.rockHit = Math.max(this.events.rockHit, -vn);
      if (-vn > SPIN_OUT_IMPACT) this.spinOut(nx, nz);
    } else if (vn < 0) {
      // Resting contact (e.g. steering into it): slide along, don't stick.
      this.velocity.x -= nx * vn;
      this.velocity.z -= nz * vn;
    }
  }

  /** Knock the duck into a short spin where it can't steer. */
  spinOut(nx: number, nz: number): void {
    if (this.shieldTime > 0) {
      this.popShield();
      return;
    }
    if (this.stun > 0) return;
    this.stun = this.tune?.spinTime ?? 0.6;
    this.spinVel = (nx * this.velocity.z - nz * this.velocity.x > 0 ? 1 : -1) * 16;
    this.velocity.multiplyScalar(this.tune?.spinKeep ?? 0.5);
    this.recover = this.tune?.recoverTime ?? 0;
    this.events.spunOut = true;
  }

  private popShield(): void {
    this.shieldTime = 0;
    this.events.shieldPopped = true;
    this.squash = Math.min(1, this.squash + 0.5);
  }

  /** Height above the water surface (0 on the water). */
  get heightAboveWater(): number {
    return this.position.y - this.waterY;
  }

  /** In the air off a waterfall (as opposed to a little wave hop). */
  get bigAir(): boolean {
    return this.airborne && this.big;
  }

  bump(amount: number): void {
    this.squash = Math.min(1, this.squash + amount);
  }

  /** Steer in [-1, 1]; `live` false holds the duck in place (countdown). */
  update(delta: number, steer: number, live: boolean, river: River, tuning: Tuning): void {
    this.tune = tuning;
    this.events.rockHit = 0;
    this.events.bankHit = 0;
    this.events.ejected = false;
    this.events.launched = false;
    this.events.landed = 0;
    this.events.spunOut = false;
    this.events.shieldPopped = false;
    this.shieldTime = Math.max(0, this.shieldTime - delta);
    river.flowAt(this.position.x, this.position.z, this.flow);

    if (!live) {
      this.waterY = river.heightAt(this.distance, this.position.x);
      this.position.y = this.waterY;
    }

    if (live) {
      this.glide = Math.max(0, this.glide - delta);
      this.stun = Math.max(0, this.stun - delta);
      // In the air the water can't grab you; stunned ducks can't steer.
      if (!this.airborne) {
        const gripRate = this.glide > 0 ? tuning.grip * 0.3 : tuning.grip;
        const grip = 1 - Math.exp(-gripRate * delta);
        this.velocity.x += (this.flow.x - this.velocity.x) * grip;
        this.velocity.z += (this.flow.z - this.velocity.z) * grip;
      }
      if (this.stun > 0) steer = 0;
      else if (this.airborne) steer *= AIR_STEER;

      const dir = river.direction(this.distance, this.dir);
      // Bounce-back: once a spin ends, the duck picks itself up and paddles
      // back toward cruising speed on its own, so a hit stings but doesn't drag.
      if (this.recover > 0 && this.stun <= 0) {
        this.recover = Math.max(0, this.recover - delta);
        const target = tuning.flapCap + 0.6;
        const forward = this.velocity.x * dir.x + this.velocity.z * dir.z;
        if (!this.airborne && forward < target) {
          const push = tuning.recoverAccel * Math.min(1, (target - forward) / 1.5) * delta;
          this.velocity.x += dir.x * push;
          this.velocity.z += dir.z * push;
        }
      }
      // Right of the downstream direction, as seen from the chase camera.
      const rightX = -dir.z;
      const rightZ = dir.x;
      this.velocity.x += rightX * steer * tuning.nudgeAccel * delta;
      this.velocity.z += rightZ * steer * tuning.nudgeAccel * delta;

      // A whirlpool costs seconds, never the race: after a while it spits you out.
      this.whirlTime = this.flow.whirl > 0.05 && !this.airborne ? this.whirlTime + delta : 0;
      if (this.whirlTime > WHIRL_EJECT_SECONDS) {
        this.whirlTime = 0;
        this.velocity.x = dir.x * 9;
        this.velocity.z = dir.z * 9;
        this.events.ejected = true;
      }

      this.position.x += this.velocity.x * delta;
      this.position.z += this.velocity.z * delta;
      this.updateHeight(delta, river);
      this.collide(river);
    }

    this.animate(delta, steer, live);
  }

  private updateHeight(delta: number, river: River): void {
    const previous = this.waterY;
    this.waterY = river.heightAt(this.distance, this.position.x);
    if (delta > 0) this.surfaceVy = (this.waterY - previous) / delta;
    if (this.airborne) {
      this.vy -= GRAVITY * delta;
      this.position.y += this.vy * delta;
      if (this.position.y <= this.waterY) {
        this.events.landed = -this.vy;
        this.position.y = this.waterY;
        this.vy = 0;
        this.airborne = false;
        this.squash = Math.min(1, this.squash + 0.8);
      }
    } else if (river.nearDrop(this.distance) && this.waterY < this.position.y - 0.12) {
      // Off a waterfall lip: a proper launch, then gravity takes over.
      this.airborne = true;
      this.big = true;
      this.vy = 2 + Math.hypot(this.velocity.x, this.velocity.z) * 0.25;
      this.events.launched = true;
    } else if (this.waterY < this.position.y - 0.04 && this.lastClimb > 0.6) {
      // Over a wave crest fast enough to leave the water: a small hop.
      this.airborne = true;
      this.big = false;
      this.vy = this.lastClimb * 1.4;
    } else {
      this.position.y = this.waterY;
      this.lastClimb = this.surfaceVy;
    }
  }

  private collide(river: River): void {
    for (const rock of river.rocks) {
      // Clear the rock if you're flying over its top.
      if (this.position.y - this.waterY > rock.r * 0.7) continue;
      const dx = this.position.x - rock.x;
      const dz = this.position.z - rock.z;
      const minDist = rock.r + DUCK_RADIUS;
      const distSq = dx * dx + dz * dz;
      if (distSq >= minDist * minDist) continue;
      const dist = Math.max(0.001, Math.sqrt(distSq));
      const nx = dx / dist;
      const nz = dz / dist;
      this.contact(nx, nz, rock.x + nx * minDist, rock.z + nz * minDist, 0, 0);
    }

    const d = this.distance;
    const hw = river.halfWidth(d);
    const cx = river.centerX(d);
    const limit = hw - DUCK_RADIUS - 0.15;
    const offset = this.position.x - cx;
    if (Math.abs(offset) > limit) {
      const side = Math.sign(offset);
      this.position.x = cx + side * limit;
      const into = this.velocity.x * side;
      if (into > 0) {
        this.velocity.x -= side * into * 1.3;
        // Scrub speed only on a real impact, not while resting against the bank.
        if (into > 1) this.velocity.z *= 0.85;
        this.events.bankHit = into;
      }
    }

    // The split's island behaves like a bank on both of its shores.
    const islandX = river.islandX(d);
    if (islandX !== null) {
      const reach = river.islandHalfWidth(d) + DUCK_RADIUS;
      const off = this.position.x - islandX;
      if (Math.abs(off) < reach) {
        const side = off >= 0 ? 1 : -1;
        this.position.x = islandX + side * reach;
        const into = -this.velocity.x * side;
        if (into > 0) {
          this.velocity.x += side * into * 1.3;
          if (into > 1) this.velocity.z *= 0.85;
          this.events.bankHit = Math.max(this.events.bankHit, into);
        }
      }
    }
  }

  private animate(delta: number, steer: number, live: boolean): void {
    this.bobTime += delta;
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    if (live && speed > 0.3) {
      const target = Math.atan2(-this.velocity.x, -this.velocity.z);
      let diff = target - this.yaw;
      diff = Math.atan2(Math.sin(diff), Math.cos(diff));
      this.yaw += diff * (1 - Math.exp(-6 * delta));
    }
    // Whirlpools spin the duck on the spot, which is the tell that you're stuck.
    this.spin += (this.flow.whirl * 9 + this.spinVel) * delta;
    this.spinVel *= Math.exp(-4 * delta);
    this.spin *= Math.exp(-(this.flow.whirl > 0 || Math.abs(this.spinVel) > 1 ? 0 : 3) * delta);
    this.roll += (-steer * 0.22 - this.roll) * (1 - Math.exp(-8 * delta));
    this.squash *= Math.exp(-7 * delta);
    this.syncVisuals(delta);
  }

  private syncVisuals(_delta: number): void {
    this.group.position.copy(this.position);
    this.model.rotation.set(0, this.yaw + this.spin, 0);
    this.model.rotation.z = this.roll;
    this.model.position.y = Math.sin(this.bobTime * 3.1) * 0.035;
    // Nose down while falling; bob on the water.
    this.model.rotation.x = this.airborne
      ? Math.max(-0.6, Math.min(0.3, this.vy * 0.06))
      : Math.sin(this.bobTime * 2.3) * 0.04;
    this.shadow.position.y = this.waterY - this.position.y + 0.03;
    // Bubble wobbles, and flickers in its last two seconds.
    this.bubble.visible = this.shieldTime > 0 && (this.shieldTime > 2 || Math.sin(this.bobTime * 30) > 0);
    if (this.bubble.visible) this.bubble.scale.set(1 + Math.sin(this.bobTime * 5) * 0.04, 1 - Math.sin(this.bobTime * 5) * 0.04, 1);
    const height = Math.max(0, this.position.y - this.waterY);
    this.shadow.scale.setScalar(1 / (1 + height * 0.35));
    const s = this.squash;
    this.model.scale.set(1 + s * 0.35, 1 - s * 0.3, 1 + s * 0.35);
  }

  stabilizeVisuals(): void {
    this.bobTime = 0;
    this.squash = 0;
    this.syncVisuals(0);
  }

  dispose(): void {
    disposeObject3D(this.group);
  }
}
